import { useState } from "react";
import useLabels from "../../hooks/useLabels";
import { ROLES } from "../../constants/base";
import { Icon } from "../../components/Icon";
import { resolveRowSubstanceId, buildWineMakingModalValues } from "../../services/common";

const EditCell = ({ row, column, table }) => {
  const [getLabel] = useLabels();
  const meta = table.options.meta;
  const columnMeta = column.columnDef.meta;
  // Exams (analysis) may be out of the recommended range -> warning only, confirm stays allowed.
  // Treatments/substances must NOT be confirmable while the blocking quantity error is shown.
  const isExam = row.original?.opType === "ANALYSIS";
  // Must match the editable Input's registered name in TableCell.
  const errorKey = `${resolveRowSubstanceId(row.original)}_${
    row.original.tank_id ?? row.original.batch_id ?? row.index
  }`;
  const isDisabled = isExam
    ? false
    : !!meta.getErrors()?.column?.[errorKey]?.value;

  const isEdit = columnMeta?.isEditable === true;
  const isOnlyManual = columnMeta?.isOnlyManual === true;
  const isRepetitiveValid =
    ["ANALYSIS", "TREATMENT", "PROTOCOL", "WINEMAKINGPROTOCOL"].includes(
      row.original?.opType
    ) &&
    (!row.original?.repetitive ||
      (row.original?.repetitive && row.original?.isEditable));
    const [id, setId] = useState(row.original.substance_id || row.original.value);

  const setRowData = () => { 
    meta.modalHandler(row.original.id);
  }

  const setWineMakingRowData = () => {
    // "day" is stored 0-indexed (day 0 = protocol start) but shown to the user as
    // 1-indexed (day 1 = today), except for CIP where it's a step number already
    // 1-indexed with no conversion. meta.formType carries the parent protocol type
    // ("CIP" or the day-based type) - see Table.js/WineMakingProtocolForm.js.
    const isDayBasedProtocol = meta?.formType && meta.formType !== "CIP";
    const values = buildWineMakingModalValues(row.original, { getLabel, isDayBasedProtocol });
    Object.entries(values).forEach(([key, value]) => meta?.setValue(key, value));

    meta.setModifyMode(row.id);
    const modalType = (meta?.type === "treatment" && columnMeta?.function_type === "CONFIRM") ? "WINEMAKING_OPERATIONS" : null;
    meta.modalHandler(row.id, modalType);
  };

  const setEditedRows = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const elName = e.currentTarget.name;

    // When entering edit mode, save the current value as "old" and "new" BEFORE any early returns
    if (elName === "edit") {
      const currentValue = row.original?.quantity || row.original?.value || "";
      // Use substance_id directly (id is from winemaking_protocol_operations table, not substance).
      // resolveRowSubstanceId handles `product` being the drug object (-> product.id) instead of NaN.
      const substanceId = resolveRowSubstanceId(row.original);

      meta?.setValue(`column.${substanceId}.old`, currentValue);
      meta?.setValue(`column.${substanceId}.new`, currentValue);
      meta?.setValue(`column.${substanceId}.value`, currentValue);
      meta?.setValue(`column.${substanceId}.substance_id`, substanceId);
    }

    if ("winemaking" === meta.type) {
      setWineMakingRowData();
      return;
    } else if (["CAPS", "BOTTLES", "TREATMENTS", "STOCK", "MACHINERIES", "FILTERS"].includes(meta.type)) {
      setRowData();
      return;
    }

    if (isDisabled && elName === "done") return;

    // update assigned drug batches when quantity changes
    if (columnMeta.function_type === "CONFIRM" && elName === "done" && columnMeta?.onConfirm) {
      const substanceId = resolveRowSubstanceId(row.original);
      columnMeta.onConfirm(substanceId);
    }
    meta?.setEditedRows((old) => ({
      ...old,
      [row.id]: !old[row.id],
    }));

    if (elName !== "edit") {
      const substanceId = resolveRowSubstanceId(row.original);
      meta?.revertData(row.index, elName === "cancel", substanceId);
    }
    e.stopPropagation();
  };

  const removeRow = () => {
    meta?.removeRow(row.index);
  };

  // Shared (client_name = "ALL") drugs, e.g. H2O, are global and must not be
  // edited or deleted from a single client's warehouse.
  const isImmutable = row.original?.client_name === "ALL";

  const setCheckbox = (e, row) => {
    // Toggle TanStack state
    row.getToggleSelectedHandler()(e);

    // Aggiorna anche React Hook Form
    const newCheckboxState = !meta?.getValues(`edit.${row.original.id}`);
    meta?.setValue(`edit.${row.original.id}`, newCheckboxState);
  };

  // const setCheckbox = (e, row) => {
  //   row.getToggleSelectedHandler();
  //   const newCheckboxState = !meta?.getValues(`edit.${row.original.id}`);

  //   console.log('table.getSelectedRowModel().rows', table.getSelectedRowModel()?.rows)
  //   meta?.setValue(`edit.${row.original.id}`, newCheckboxState);   // tank_id or batch_id in case of analysis/treatment on batch
  // }

  const modalHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (row.original.opType === "WINEMAKINGPROTOCOL") setWineMakingRowData();
    else if (columnMeta?.type === "drugBatchUpdate") {
      const modalType = "BATCH_SELECTOR";
      meta.modalHandler(id, modalType);
    } else  meta?.modalHandler(row.original.id);

  }

  if (isEdit && isOnlyManual && row.original?.protocolType === 'MANUAL') {
    return (
      <div className="edit-cell-container">
        <div className="edit-cell-action">
          <button onClick={setEditedRows} name="edit">
          <Icon icon="fa-gear" />
          </button>
      </div>
    </div>
    )
  } else if (isEdit && isOnlyManual && row.original?.protocolType === 'TANKS_PROTOCOL')
      return (<input
        type="checkbox"
        checked={meta?.getValues(`edit.${row.original.id}`)}
        onChange={(e) => setCheckbox(e, row)}
      />)
  else if (isOnlyManual) return;

  return (
    (isRepetitiveValid || ['PROTOCOL', 'CAPS', 'BOTTLES', 'TREATMENTS', 'STOCK', 'MACHINERIES', 'FILTERS', 'WINEMAKINGPROTOCOL'].includes(row.original?.opType)) && (
      <div className="edit-cell-container">
        {meta?.editedRows[row.id] ? (
          <div className="edit-cell-action">
            <button onClick={setEditedRows} name="cancel">
              −
            </button>{" "}
            <button
              onClick={setEditedRows}
              name="done"
              style={isDisabled ? { cursor: "not-allowed" } : {}}
              disabled={isDisabled}
            >
              ✔
            </button>
          </div>
        ) : (
          <div className="edit-cell-action"> 
            {/* {isEdit && row.original?.opType !== 'PROTOCOL' && ( */}
            {isEdit && meta?.type !== 'newAnalysisProtocol' && meta?.type !== 'analysis' && (row.original.opType !== "TREATMENTS" || meta?.type === "treatments" || (meta?.type === "treatmentApplicationUser" && columnMeta?.type === "drugBatchUpdate" && Array.isArray(row.original?.drug_batches) && row.original?.drug_batches?.length > 0)) && (
              <button
              onClick={(e) =>
                isImmutable ? e.preventDefault() :
                (['CAPS', 'BOTTLES', 'TREATMENTS', 'STOCK', 'MACHINERIES', 'FILTERS', 'WINEMAKINGPROTOCOL'].includes(row.original.opType)) ?
              modalHandler(e) : setEditedRows(e)}
              name="edit"
              disabled={isImmutable}
              style={isImmutable ? { cursor: "not-allowed" } : {}}
              >
                {!["ANALYSIS"].includes(row.original?.opType) ? (
                  "✐"
                ) : <Icon icon="fa-flask-vial" />
                }
              </button>
            )}
            {columnMeta?.role === ROLES.CLIENT && !["ANALYSIS", "TREATMENT", "STOCK"].includes(row.original?.opType) && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!isImmutable) removeRow();
                }}
                name="remove"
                disabled={isImmutable}
                style={isImmutable ? { cursor: "not-allowed" } : {}}
              >
                X
              </button>
            )}
          </div>
        )}
        {!["ANALYSIS", "TREATMENT", "CAPS", "BOTTLES", "TREATMENTS", "STOCK", "PROTOCOL", "MACHINERIES", "FILTERS"].includes(row.original?.opType) && (
          <input
            type="checkbox"
            checked={meta?.getValues(`edit.${row.original.id}`) ?? row.getIsSelected()}
            onChange={(e) => setCheckbox(e, row)}

            // checked={row.getIsSelected()}
            // checked={meta?.getValues(`edit.${row.original.id}`)}
            // onChange={row.getToggleSelectedHandler()}
            // onChange={(e) => setCheckbox(e, row)}
          />
        )}
      </div>
    )
  );
};

export default EditCell;
